import type { ProductPreviewSupplierDocuments } from '@travel-planner/shared/schemas';
import { ExternalLink, FileText, Star } from 'lucide-react';
import { Text } from '@/shared/ui';
import css from './supplier-documents-panel.module.scss';

type SupplierDocumentsPanelProps = {
  data: ProductPreviewSupplierDocuments | undefined;
  isLoading: boolean;
  isError: boolean;
  // Mints a short-lived URL and opens it. Owned by the modal so both sources
  // share one opening path — the consultant should not learn two workflows
  // (Melhoria 6 §5).
  onOpen: (documentId: string) => void;
  isOpening: boolean;
};

/**
 * "Supplier documents" — the CRM-sourced half of the product preview's
 * Attachments tab, rendered ABOVE the product's own documents (Melhoria 6 §2).
 *
 * The section stays visible in every state, including empty and unlinked: a
 * consultant needs to be able to tell "this supplier has no contract on file"
 * from "this product has no supplier", and a section that disappears answers
 * neither question (§8).
 *
 * Its loading and error states are local, so a CRM failure leaves the product
 * documents below it fully readable (§8, last line).
 */
export const SupplierDocumentsPanel = ({
  data,
  isLoading,
  isError,
  onOpen,
  isOpening,
}: SupplierDocumentsPanelProps) => {
  const documents = data?.documents ?? [];

  return (
    <section aria-labelledby="supplier-documents-heading" className={css.root}>
      <div className={css.head}>
        <div className={css.headText}>
          {/* Source labels are visible text, never colour alone (§15). */}
          <span className={css.badge}>Supplier CRM</span>
          <div>
            <h4 className={css.title} id="supplier-documents-heading">
              Supplier documents
            </h4>
            <Text as="p" size="xs" variant="muted">
              {data?.supplierName
                ? `Shared by ${data.supplierName} and available to every linked product.`
                : 'Shared by the supplier and available to every linked product.'}
            </Text>
          </div>
        </div>
        {documents.length > 0 && (
          <Text as="span" size="xs" variant="muted">
            {documents.length} {documents.length === 1 ? 'file' : 'files'}
          </Text>
        )}
      </div>

      {isLoading && (
        <Text as="p" size="sm" variant="muted">
          Loading supplier documents…
        </Text>
      )}

      {/* A CRM read failure is reported in place. It must not take the product
          documents down with it, so this returns early only for THIS section. */}
      {!isLoading && isError && (
        <Text as="p" size="sm" variant="error">
          Supplier documents could not be loaded.
        </Text>
      )}

      {!isLoading && !isError && data && !data.linked && (
        <Text as="p" size="sm" variant="muted">
          This product is not linked to a CRM supplier.
        </Text>
      )}

      {!isLoading && !isError && data?.unavailable && (
        <Text as="p" size="sm" variant="muted">
          The linked supplier record is unavailable.
        </Text>
      )}

      {!isLoading &&
        !isError &&
        data?.linked &&
        !data.unavailable &&
        documents.length === 0 && (
          <Text as="p" size="sm" variant="muted">
            No supplier documents are available in CRM.
          </Text>
        )}

      {!isLoading && !isError && documents.length > 0 && (
        <div className={css.scroll}>
          <table className={css.table}>
            <thead>
              <tr>
                <th scope="col">File</th>
                <th scope="col">Category</th>
                <th scope="col">Visibility</th>
                <th className={css.actionCol} scope="col">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.documentId}>
                  <td>
                    <span className={css.file}>
                      <FileText className={css.fileIcon} size={18} />
                      <span className={css.fileText}>
                        {/* A button, not an anchor: the URL is minted per click
                            by the server, so there is no href to publish. */}
                        <button
                          className={css.fileLink}
                          disabled={isOpening}
                          onClick={() => onOpen(doc.documentId)}
                          type="button"
                        >
                          {doc.name}
                        </button>
                        {doc.featured && (
                          <span className={css.featured}>
                            <Star fill="currentColor" size={11} />
                            Featured
                          </span>
                        )}
                      </span>
                    </span>
                  </td>
                  <td>{doc.category}</td>
                  {/* Informational only — the preview is read-only and never
                      changes the stored visibility (§6). */}
                  <td>
                    {doc.visibility === 'Internal' ? 'Internal' : 'Product'}
                  </td>
                  <td className={css.actionCol}>
                    <button
                      aria-label={`Open ${doc.name} in a new tab`}
                      className={css.open}
                      disabled={isOpening}
                      onClick={() => onOpen(doc.documentId)}
                      type="button"
                    >
                      Open
                      <ExternalLink size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
